import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

/** Icon in a soft tinted circle; the colour carries the meaning, the toast itself stays neutral. */
function ToastIcon({ className, children }: { className: string; children: ReactNode }) {
  return <span className={`flex size-8 items-center justify-center rounded-full ${className}`}>{children}</span>
}

/**
 * App toaster. Toasts use the surface colour with a thin accent bar and a tinted icon
 * (green success, red error, amber warning, brand info) instead of a fully coloured background.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      gap={10}
      icons={{
        success: (
          <ToastIcon className="bg-success/12 text-success">
            <CircleCheckIcon className="size-4" />
          </ToastIcon>
        ),
        info: (
          <ToastIcon className="bg-primary/12 text-primary">
            <InfoIcon className="size-4" />
          </ToastIcon>
        ),
        warning: (
          <ToastIcon className="bg-amber-500/12 text-amber-600">
            <TriangleAlertIcon className="size-4" />
          </ToastIcon>
        ),
        error: (
          <ToastIcon className="bg-error/12 text-error">
            <OctagonXIcon className="size-4" />
          </ToastIcon>
        ),
        loading: (
          <ToastIcon className="bg-bg-secondary text-text-secondary">
            <Loader2Icon className="size-4 animate-spin" />
          </ToastIcon>
        ),
      }}
      toastOptions={{
        classNames: {
          toast:
            "group/toast !items-start !gap-3 !rounded-card !border !border-line !bg-surface-elevated !p-3.5 !pl-4 !text-text-primary !shadow-elevated overflow-hidden before:absolute before:inset-y-0 before:left-0 before:w-1",
          icon: "!m-0 !size-8 shrink-0",
          content: "!gap-0.5 pt-1",
          title: "!text-sm !font-semibold !leading-snug",
          description: "!text-[13px] !leading-snug !text-text-secondary",
          closeButton:
            "!left-auto !right-2 !top-2 !translate-x-0 !translate-y-0 !border-none !bg-transparent !text-text-tertiary hover:!bg-bg-secondary hover:!text-text-primary",
          success: "before:bg-success",
          error: "before:bg-error",
          warning: "before:bg-amber-500",
          info: "before:bg-primary",
          actionButton: "!bg-primary !text-primary-foreground",
          cancelButton: "!bg-bg-secondary !text-text-secondary",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
